/**
 * Storage Utilities
 * Handles saving and loading user progress to/from localStorage
 */

const STORAGE_KEY = 'realityDecoder_progress';

/**
 * Get the default empty progress structure
 */
const getEmptyProgress = () => ({
  lastUpdated: new Date().toISOString(),
  issues: {},
  settings: {
    showToolModals: true
  }
});

/**
 * Load progress from localStorage
 * @returns {Object} User progress data
 */
export const loadProgress = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return getEmptyProgress();
    }
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error loading progress from localStorage:', error);
    return getEmptyProgress();
  }
};

/**
 * Save progress to localStorage
 * @param {Object} progress - Progress data to save
 */
export const saveProgress = (progress) => {
  try {
    const updatedProgress = {
      ...progress,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedProgress));
  } catch (error) {
    console.error('Error saving progress to localStorage:', error);
  }
};

/**
 * Update a specific issue's progress
 * @param {string} issueId - The issue ID (e.g., 'EMO-001')
 * @param {Object} sectionData - The section data to update
 */
export const updateIssueProgress = (issueId, sectionData) => {
  const progress = loadProgress();
  
  if (!progress.issues[issueId]) {
    progress.issues[issueId] = {
      title: '',
      sections: {},
      currentSection: 0,
      isComplete: false
    };
  }
  
  // Update the section data
  const sectionId = sectionData.sectionId;
  progress.issues[issueId].sections[sectionId] = {
    ...progress.issues[issueId].sections[sectionId],
    ...sectionData
  };
  
  // Update current section if provided
  if (sectionData.currentSection !== undefined) {
    progress.issues[issueId].currentSection = sectionData.currentSection;
  }
  
  // Update completion status if provided
  if (sectionData.isComplete !== undefined) {
    progress.issues[issueId].isComplete = sectionData.isComplete;
  }
  
  saveProgress(progress);
  return progress;
};

/**
 * Get progress for a specific issue
 * @param {string} issueId - The issue ID
 * @returns {Object|null} Issue progress data
 */
export const getIssueProgress = (issueId) => {
  const progress = loadProgress();
  return progress.issues[issueId] || null;
};

/**
 * Get all user responses for an issue (for export)
 * @param {string} issueId - The issue ID
 * @returns {Object} All responses for the issue
 */
export const getIssueResponses = (issueId) => {
  const progress = loadProgress();
  return progress.issues[issueId] || { sections: {} };
};

/**
 * Clear progress for a specific issue
 * @param {string} issueId - The issue ID
 */
export const clearIssueProgress = (issueId) => {
  const progress = loadProgress();
  delete progress.issues[issueId];
  saveProgress(progress);
};

/**
 * Clear all progress data
 */
export const clearAllProgress = () => {
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Get all issues that have progress saved
 * @returns {Array} List of issue IDs with saved progress
 */
export const getSavedIssues = () => {
  const progress = loadProgress();
  return Object.keys(progress.issues);
};

/**
 * Update settings
 * @param {Object} settings - Settings to update
 */
export const updateSettings = (settings) => {
  const progress = loadProgress();
  progress.settings = {
    ...progress.settings,
    ...settings
  };
  saveProgress(progress);
};

/**
 * Get settings
 * @returns {Object} User settings
 */
export const getSettings = () => {
  const progress = loadProgress();
  return progress.settings;
};

export default {
  loadProgress,
  saveProgress,
  updateIssueProgress,
  getIssueProgress,
  getIssueResponses,
  clearIssueProgress,
  clearAllProgress,
  getSavedIssues,
  updateSettings,
  getSettings
};